<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { MULTI_CONSENT_CONDITION_TITLES_BY_TYPE } from "@core/constants"
import { formatDate } from "@core/helpers"

// ================================
// TYPES & INTERFACES
// ================================

export type ConsentConditionItem = {
    title: string
    extraTitle?: string
}

type Props = {
    /** v-model : true quand toutes les autorisations sont accordées */
    modelValue: boolean
    /** Type de compteur : électricité seul, gaz seul, ou les deux (multi ENEDIS / GRDF) */
    type: "pdl" | "pce" | "both"
    /** Durée du consentement en mois (défaut 5 * 12) */
    duration?: number
    /** Date de début du consentement (défaut: aujourd'hui) */
    dateFrom?: string | Date
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(defineProps<Props>(), {
    duration: 5 * 12,
    dateFrom: undefined,
})

const emit = defineEmits<{ "update:modelValue": [value: boolean] }>()

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            paragraph:
                "Pour pouvoir récupérer vos données de consommation, les analyser et vous conseiller sur votre consommation énergétique nous avons besoin de votre autorisation.",
            paragraph_date_pdl:
                "Nous autorisez-vous à demander auprès d'ENEDIS les données ci-dessous, à partir d'aujourd'hui, soit le {dateFrom} et pendant une durée de {duration}, soit jusqu'au {dateTo}. (Les données historiques de consommation pourront être demandées jusqu'à 3 ans dans le passé soit à partir du {dateFromData}) :",
            paragraph_date_pce:
                "Nous autorisez-vous à demander auprès de GRDF les données ci-dessous, à partir d'aujourd'hui, soit le {dateFrom} et pendant une durée de {duration}, soit jusqu'au {dateTo}. (Les données historiques de consommations publiées et informatives seront demandées à partir du {dateFromData}) :",
            paragraph_date_both:
                "Nous autorisez-vous à demander auprès d'ENEDIS et de GRDF les données ci-dessous, à partir d'aujourd'hui, soit le {dateFrom} et pendant une durée de {duration}, soit jusqu'au {dateTo}. (Les données historiques de consommations publiées et informatives seront demandées à partir du {dateFromData}) :",
            paragraph_consent:
                "En donnant votre consentement, vous acceptez expressément que vos données personnelles soient conservées à des fins de gestion et de traçabilité pendant une durée de 3 ans après la date de fin de consentement. Vous pourrez à tout moment révoquer votre consentement dans votre espace client ou en contactant Homeys à l'adresse rgpd{'@'}homeys.io.",
            accept_label: "J'accepte les conditions du consentement",
            all_authorized_label: "Tout autoriser",
        },
    },
})

// ================================
// COMPUTED – dates et durée
// ================================

const baseDate = computed(() => {
    const d = props.dateFrom
    if (d instanceof Date) return d
    if (typeof d === "string") return new Date(d)
    return new Date()
})

const dateFromFormatted = computed(() =>
    formatDate(baseDate.value, "DD/MM/YYYY")
)

const dateToComputed = computed(() => {
    const d = new Date(baseDate.value)
    d.setMonth(d.getMonth() + props.duration)
    return d
})

const dateToFormatted = computed(() =>
    formatDate(dateToComputed.value, "DD/MM/YYYY")
)

const dateFromDataComputed = computed(() => {
    const d = new Date(baseDate.value)
    d.setFullYear(d.getFullYear() - 3)
    return d
})

const dateFromDataFormatted = computed(() =>
    formatDate(dateFromDataComputed.value, "DD/MM/YYYY")
)

/** Durée formatée (ex. "5 ans" pour 60 mois) */
const durationFormatted = computed(() => {
    const months = props.duration
    if (months >= 12) {
        const years = Math.round(months / 12)
        return years === 1 ? "1 an" : `${years} ans`
    }
    return `${months} mois`
})

const paragraphDateText = computed(() => {
    const key =
        props.type === "both"
            ? "paragraph_date_both"
            : props.type === "pdl"
              ? "paragraph_date_pdl"
              : "paragraph_date_pce"
    return t(key, {
        dateFrom: dateFromFormatted.value,
        dateTo: dateToFormatted.value,
        dateFromData: dateFromDataFormatted.value,
        duration: durationFormatted.value,
    })
})

// ================================
// COMPUTED – liste des conditions
// ================================

const conditionsPdl: ConsentConditionItem[] = [
    {
        title: "L'historique des consommations, en kWh, les puissances atteintes et les dépassements de puissance.",
    },
    {
        title: "L'historique des relevés d'index quotidiens, en kWh, et la puissance maximale quotidienne, en kVA ou kW.",
    },
    {
        title: "L'historique de courbe de charge jusqu'au pas restitué par le distributeur",
        extraTitle:
            "(et l'activation de la collecte de la courbe de charge si celle-ci n'est pas activée).",
    },
    {
        title: "Les données techniques et contractuelles disponibles.",
    },
]

const conditionsPce: ConsentConditionItem[] = [
    {
        title: "Les données publiées",
        extraTitle:
            "(données transmises à mon fournisseur d'énergie pour la facturation).",
    },
    {
        title: "Les données informatives journalières",
        extraTitle: "(si disponible).",
    },
    {
        title: "Les données contractuelles",
        extraTitle: "(données relatives au contrat avec mon fournisseur).",
    },
    {
        title: "Les données techniques",
        extraTitle:
            "(données relatives aux détails techniques de mon compteur).",
    },
]

/**
 * Points à cocher : listes structurées PDL/PCE, ou libellés multi (ENEDIS + GRDF) pour `both`.
 */
const conditionsList = computed((): ConsentConditionItem[] => {
    if (props.type === "both") {
        return MULTI_CONSENT_CONDITION_TITLES_BY_TYPE.both.map((title) => ({
            title,
        }))
    }
    return props.type === "pdl" ? conditionsPdl : conditionsPce
})

// ================================
// TOGGLES : "Tout autoriser" + un toggle par point
// ================================

/** État de chaque point (une entrée par condition). Initialisé depuis modelValue. */
const pointStates = ref<boolean[]>(
    Array.from({ length: conditionsList.value.length }, () => props.modelValue)
)

/**
 * Changement de type (pdl / pce / both) : nouvelle liste de points, tout décoché.
 */
watch(
    () => props.type,
    () => {
        pointStates.value = Array.from(
            { length: conditionsList.value.length },
            () => false
        )
        emit("update:modelValue", false)
    }
)

/** Synchroniser pointStates quand le parent change modelValue (ex. reset). */
watch(
    () => props.modelValue,
    (val) => {
        const allSame = pointStates.value.every((s) => s === val)
        if (!allSame) {
            pointStates.value = pointStates.value.map(() => val)
        }
    }
)

/** True quand tous les points sont à true. En set : met tous les points à la valeur et émet. */
const allAcceptedModel = computed({
    get: () => pointStates.value.every(Boolean),
    set: (v: boolean) => {
        pointStates.value = pointStates.value.map(() => v)
        emit("update:modelValue", v)
    },
})

/** Met à jour un point et émet true seulement si tous sont true. */
function setPoint(index: number, value: boolean) {
    pointStates.value = pointStates.value.map((s, i) =>
        i === index ? value : s
    )
    const all = pointStates.value.every(Boolean)
    emit("update:modelValue", all)
}
</script>

<template>
    <div
        class="source-consent-sign-conditions flex flex-col gap-6 rounded-lg border border-gray-200 bg-gray-50 p-4"
    >
        <p class="text-sm leading-relaxed text-gray-700">
            {{ t("paragraph") }}
        </p>
        <p class="text-sm leading-relaxed text-gray-700">
            {{ paragraphDateText }}
        </p>

        <div
            class="flex flex-wrap items-center gap-3 rounded border border-gray-200 bg-white p-3"
        >
            <BaseToggleSwitch
                :id="`source-consent-all-authorized`"
                v-model="allAcceptedModel"
                :aria-label="t('all_authorized_label')"
            />
            <label
                :for="`source-consent-all-authorized`"
                class="cursor-pointer text-sm font-medium text-gray-800"
            >
                {{ t("all_authorized_label") }}
            </label>
        </div>

        <ul class="flex flex-col gap-3 text-sm text-gray-800">
            <li
                v-for="(item, index) in conditionsList"
                :key="index"
                class="flex flex-wrap items-center gap-3 rounded border border-gray-200 bg-white p-3"
            >
                <BaseToggleSwitch
                    :id="`source-consent-point-${index}`"
                    :model-value="pointStates[index]"
                    :aria-label="item.title"
                    @update:model-value="setPoint(index, $event)"
                />
                <span class="min-w-0 flex-1 leading-relaxed">
                    {{ item.title }}
                    <span v-if="item.extraTitle" class="font-medium">
                        {{ item.extraTitle }}
                    </span>
                </span>
            </li>
        </ul>

        <p class="text-sm leading-relaxed text-gray-600">
            {{ t("paragraph_consent") }}
        </p>
    </div>
</template>
